import { memo } from "react";

/**
 * Loading indicator — four bars rise and fall in a left-to-right wave.
 * Pure CSS, GPU-composited (transform + opacity only). Honors
 * `prefers-reduced-motion` via a fallback pulse in globals.css.
 */

export type SpinnerSize = "sm" | "md" | "lg";

interface SpinnerProps {
  size?: SpinnerSize;
  className?: string;
  label?: string;
}

const SIZES: Record<SpinnerSize, { container: string; bar: string }> = {
  sm: { container: "h-4 gap-[2px]", bar: "w-[2px]" },
  md: { container: "h-6 gap-[3px]", bar: "w-[3px]" },
  lg: { container: "h-8 gap-[4px]", bar: "w-[4px]" },
};

// Delay + peak height are CSS variables so the .mi-bar animation
// shorthand picks them up regardless of @layer cascade ordering.
const BARS = [
  { delay: "[--mi-delay:0ms]", peak: "[--mi-peak:0.7]" },
  { delay: "[--mi-delay:300ms]", peak: "[--mi-peak:1]" },
  { delay: "[--mi-delay:600ms]", peak: "[--mi-peak:0.55]" },
  { delay: "[--mi-delay:900ms]", peak: "[--mi-peak:0.85]" },
] as const;

function SpinnerImpl({
  size = "md",
  className,
  label = "Loading",
}: SpinnerProps) {
  const s = SIZES[size];
  return (
    <div
      role="status"
      aria-label={label}
      className={`inline-flex items-end ${s.container}${className ? ` ${className}` : ""}`}
    >
      {BARS.map((b) => (
        <span
          key={b.delay}
          className={`mi-bar block h-full rounded-[1px] bg-accent ${s.bar} ${b.delay} ${b.peak}`}
        />
      ))}
    </div>
  );
}

export const Spinner = memo(SpinnerImpl);
